// New Scenario creation dialog — styled to match NewClientDialog so the
// Scenarios & Staff Plans landing has a consistent "create" experience.
//
// Replaces the prior `prompt('Name for the new scenario')` flow. Lets the
// user pick:
//   - Scenario name (required)
//   - Optional notes
//   - Template: clone the most recent scenario, pick a specific scenario,
//     or start from a blank engine default
//   - Model: required. Defaults to the most-recent scenario's model when
//     cloning, otherwise the first available model. Each scenario carries
//     its own model since the client no longer pins one.

import { useMemo, useState } from 'react';
import { Copy, FilePlus2, X } from 'lucide-react';
import type {
  Client,
  Scenario,
} from '@rc/engine/types';
import { api } from '../lib/api';
import type { ModelSummary } from '../lib/api';
import { toast } from './Toast';

interface Props {
  client: Client;
  scenarios: Scenario[];
  models: ModelSummary[];
  geoclientId?: string;
  onClose: () => void;
  onCreated: (created: Scenario) => void;
}

const BLANK = '__blank__';

function newScenarioUuid(): string {
  return crypto.randomUUID();
}

export default function NewScenarioDialog({
  client,
  scenarios,
  models,
  geoclientId,
  onClose,
  onCreated,
}: Props) {
  const sortedScenarios = useMemo(
    () =>
      [...scenarios].sort((a, b) =>
        (b.updatedAt || '').localeCompare(a.updatedAt || ''),
      ),
    [scenarios],
  );

  const defaultName = `Scenario ${scenarios.length + 1}`;
  const [name, setName] = useState(defaultName);
  const [notes, setNotes] = useState('');
  // Default model preference order:
  //   1. Most-recent existing scenario's model (likely what the user wants
  //      to clone from)
  //   2. First available model in the catalog
  // Falls back to '' when no models are loaded yet — the submit handler
  // surfaces an error in that case.
  const [modelId, setModelId] = useState<string>(
    sortedScenarios[0]?.modelId ?? models[0]?.id ?? '',
  );
  // Default to "clone latest" when at least one scenario already exists,
  // otherwise blank.
  const [templateId, setTemplateId] = useState<string>(
    sortedScenarios[0]?.id || BLANK,
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast('error', 'Scenario name is required.');
      return;
    }
    if (!modelId) {
      toast('error', 'Pick a model.');
      return;
    }
    setBusy(true);
    try {
      const newId = newScenarioUuid();
      const now = new Date().toISOString();
      let draft: Scenario;

      if (templateId !== BLANK) {
        const template =
          sortedScenarios.find((s) => s.id === templateId) ?? sortedScenarios[0];
        if (!template) throw new Error('Template scenario not found.');
        const tpl = await api.getScenario(client.id, template.id);
        draft = {
          ...tpl,
          id: newId,
          clientId: client.id,
          modelId,
          name: trimmed,
          notes: notes.trim() || `Cloned from ${tpl.name}`,
          createdAt: now,
          updatedAt: now,
        };
      } else {
        // Build a starter profile from the chosen model.
        const m = await api.getModel(modelId);
        const preset =
          m.channelMixPresets?.find(
            (p) => p.id === 'brand_plus_performance' || p.id === 'balanced',
          ) || m.channelMixPresets?.[0];
        const active = (m.channels || []).filter((c) => c.active !== false);
        const evenShare = active.length > 0 ? 1 / active.length : 0;
        const allocation: Record<string, number> = {};
        for (const ch of m.channels || []) {
          allocation[ch.id] = preset?.allocation?.[ch.id] ?? evenShare;
        }
        const selections: Record<string, string | string[]> = {};
        for (const dim of m.dimensions || []) {
          if (dim.kind === 'multiselect') {
            // Multiselect dims start empty — user opts into weight.
            selections[dim.id] = [];
            continue;
          }
          const def = dim.options[0];
          if (def) selections[dim.id] = def.value;
        }
        if (client.vertical) selections.vertical = client.vertical;
        // Seed `selections.geo` from the structured market when present:
        // - country market: ISO code (e.g. "US") — falls through to model's
        //   geo dimension as a country selector.
        // - region market: region code (e.g. "EMEA").
        // - global: leave the geo dimension at its first-option default.
        if (client.market?.code) selections.geo = client.market.code;

        draft = {
          id: newId,
          clientId: client.id,
          modelId,
          name: trimmed,
          notes: notes.trim() || 'Starter scenario.',
          createdAt: now,
          updatedAt: now,
          profile: {
            totalBudget: 50_000_000,
            channelMixPresetId: m.channelMixPresets?.[0]?.id || 'custom',
            channelAllocation: allocation,
            selections,
            activeChannels: Object.fromEntries(
              (m.channels || []).map((c) => [c.id, true]),
            ),
            adopsSupport: { programmatic: true, digital_direct: true, ctv: true },
            currentStaffByLevel: {},
          },
        };
      }

      const saved = await api.createScenario(client.id, draft, geoclientId);
      onCreated(saved);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const templateScenario =
    templateId === BLANK
      ? null
      : sortedScenarios.find((s) => s.id === templateId);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4">
      <div className="card max-w-lg w-full">
        <div className="card-header">
          <h2 className="text-base font-semibold">New Scenario</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="label flex items-center gap-1.5">
              <FilePlus2 size={12} className="text-brand-purple-500" />
              Start from
            </label>
            <select
              className="input"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value={BLANK}>Blank scenario (engine defaults)</option>
              {sortedScenarios.length > 0 && (
                <optgroup label="Clone existing scenario">
                  {sortedScenarios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="text-[11px] text-slate-500 mt-1 flex items-start gap-1">
              <Copy size={11} className="text-slate-400 shrink-0 mt-px" />
              {templateScenario
                ? `Inputs and current staff plan will be copied from "${templateScenario.name}".`
                : 'Builds a starter profile with the model\'s default channel mix and dimensions.'}
            </p>
          </div>

          <div>
            <label className="label">Scenario name</label>
            <input
              autoFocus
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aggressive growth, FY26 baseline"
            />
          </div>

          <div>
            <label className="label">Model</label>
            <select
              className="input"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              Each scenario picks its own model — different scenarios under
              this client can run against different models.
            </p>
          </div>

          <div>
            <label className="label">
              Notes <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <textarea
              className="input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What is this scenario testing?"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={submit} className="btn-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create scenario'}
          </button>
        </div>
      </div>
    </div>
  );
}
